// lib/StaffContext.tsx
// Manages the "sub-session" for RBAC Level 3 clinics.
// After clinic login, a staff member selects their name and enters a PIN.
// The resulting ActiveStaff is stored in sessionStorage and cleared on tab close.

import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "./supabase";
import type { Permission } from "./permissions";

export interface ActiveStaff {
  staff_id: string;
  name: string;
  role: string | null;
  is_clinic_admin: boolean;
  permissions: Record<Permission, boolean>;
  must_change_password?: boolean;
}

const STORAGE_KEY = "active_staff_session";

interface StaffContextValue {
  activeStaff: ActiveStaff | null;
  setActiveStaff: (staff: ActiveStaff | null) => void;
  clearStaff: () => void;
}

const StaffContext = createContext<StaffContextValue | undefined>(undefined);

export const StaffProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeStaff, setActiveStaffState] = useState<ActiveStaff | null>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? (JSON.parse(stored) as ActiveStaff) : null;
    } catch {
      return null;
    }
  });

  const setActiveStaff = (staff: ActiveStaff | null) => {
    setActiveStaffState(staff);
    if (staff) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(staff));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  };

  const clearStaff = () => setActiveStaff(null);

  // Clear staff session when Supabase signs out (current tab)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        setActiveStaffState(null);
        sessionStorage.removeItem(STORAGE_KEY);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  return (
    <StaffContext.Provider value={{ activeStaff, setActiveStaff, clearStaff }}>
      {children}
    </StaffContext.Provider>
  );
};

export const useStaff = (): StaffContextValue => {
  const ctx = useContext(StaffContext);
  if (!ctx) throw new Error("useStaff must be used inside StaffProvider");
  return ctx;
};
